import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Avatar,
  InputAdornment,
  Button,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import apiAxiosInstance from '../../utils/axiosInstance';
import GroupEditDialog from '../Behavior/Dialogs/GroupEditDialog';

interface RosterPanelProps {
  onSelect: (scope: 'student' | 'group', id: string, name: string) => void;
  selectedId?: string | null;
  selectedScope?: 'student' | 'group' | null;
  onAddStudent?: () => void;
  onAddGroup?: () => void;
  refreshTrigger?: number;
}

const RosterPanel: React.FC<RosterPanelProps> = ({
  onSelect,
  selectedId,
  selectedScope,
  onAddStudent,
  onAddGroup,
  refreshTrigger = 0,
}) => {
  const [tab, setTab] = useState<'student' | 'group'>('student');
  const [search, setSearch] = useState('');
  const [students, setStudents] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingGroup, setEditingGroup] = useState<any | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        apiAxiosInstance.get('/api/students'),
        apiAxiosInstance.get('/api/groups'),
      ]);
      setStudents(s.data || []);
      setGroups(g.data || []);
    } catch (e) {
      console.error('Failed to load roster', e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  // Toggle group expansion
  const toggleGroupExpansion = (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Filter students based on search
  const filteredStudents = useMemo(() => {
    let result = students;
    
    // Filter by search
    if (!search.trim()) return result;
    const searchLower = search.toLowerCase();
    return result.filter((s: any) => {
      const fullName = `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase();
      return fullName.includes(searchLower) || 
             (s.gradeLevel && String(s.gradeLevel).toLowerCase().includes(searchLower));
    });
  }, [students, search]);

  // Filter groups based on search
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const searchLower = search.toLowerCase();
    return groups.filter((g: any) => 
      (g.name || '').toLowerCase().includes(searchLower)
    );
  }, [groups, search]);

  const isSelected = (id: string, scope: 'student' | 'group') => {
    return selectedId === id && selectedScope === scope;
  };

  // Get students in a group
  const getGroupStudents = (group: any) => {
    if (!group.students || group.students.length === 0) return [];
    
    // Check if students are already populated (objects) or just IDs (strings)
    const firstStudent = group.students[0];
    const isPopulated = typeof firstStudent === 'object' && firstStudent._id;
    
    if (isPopulated) {
      // Already populated - return as-is
      return group.students;
    }
    
    // Not populated - look up in students array
    return group.students
      .map((studentId: string) => students.find((s: any) => s._id === studentId))
      .filter(Boolean);
  };

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Add Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">Roster</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={tab === 'student' ? onAddStudent : onAddGroup}
        >
          Add {tab === 'student' ? 'Student' : 'Session'}
        </Button>
      </Box>
      
      <Tabs value={tab} onChange={(_, v) => { setTab(v); setSearch(''); }} sx={{ mb: 2 }}>
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon fontSize="small" />
              Students
              <Chip label={filteredStudents.length} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }} />
            </Box>
          } 
          value="student" 
        />
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <GroupIcon fontSize="small" />
              Groups
              <Chip label={groups.length} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }} />
            </Box>
          } 
          value="group" 
        />
      </Tabs>

      <TextField
        size="small"
        placeholder={`Search ${tab}s...`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {tab === 'student' ? (
          <>
            {filteredStudents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {search 
                    ? 'No students found' 
                    : 'No students available. Add your first student to get started!'
                  }
                </Typography>
              </Box>
            ) : (
              <List dense>
                {filteredStudents.map((s: any) => (
                  <ListItemButton
                    key={s._id}
                    selected={isSelected(s._id, 'student')}
                    onClick={() => onSelect('student', s._id, `${s.firstName} ${s.lastName}`)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        '&:hover': { backgroundColor: 'primary.light' },
                      },
                    }}
                  >
                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.875rem' }}>
                      {(s.firstName?.[0] || '') + (s.lastName?.[0] || '')}
                    </Avatar>
                    <ListItemText
                      primary={`${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed'}
                      secondary={s.gradeLevel ? `Grade ${s.gradeLevel}` : 'No grade'}
                      primaryTypographyProps={{ fontWeight: isSelected(s._id, 'student') ? 600 : 400 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        ) : (
          <>
            {filteredGroups.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {search ? 'No groups found' : 'No groups available. Create your first group to get started!'}
                </Typography>
              </Box>
            ) : (
              <List dense>
                {filteredGroups.map((g: any) => {
                  const groupStudents = getGroupStudents(g);
                  const isExpanded = expandedGroups.has(g._id);
                  
                  return (
                    <Box key={g._id}>
                      {/* Group Header */}
                      <ListItemButton
                        selected={isSelected(g._id, 'group')}
                        onClick={() => onSelect('group', g._id, g.name || 'Unnamed Group')}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            '&:hover': { backgroundColor: 'primary.light' },
                          },
                        }}
                      >
                        <Avatar sx={{ width: 32, height: 32, mr: 1.5, bgcolor: 'secondary.main' }}>
                          <GroupIcon fontSize="small" />
                        </Avatar>
                        <ListItemText
                          primary={g.name || 'Unnamed Group'}
                          secondary={`${groupStudents.length} student${groupStudents.length !== 1 ? 's' : ''}`}
                          primaryTypographyProps={{ fontWeight: isSelected(g._id, 'group') ? 600 : 400 }}
                        />
                        
                        {/* Edit Group Members Button */}
                        <Tooltip title="Edit group members">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingGroup(g);
                            }}
                            sx={{ mr: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Expand/Collapse Button */}
                        {groupStudents.length > 0 && (
                          <Tooltip title={isExpanded ? "Hide students" : "Show students"}>
                            <IconButton
                              size="small"
                              onClick={(e) => toggleGroupExpansion(g._id, e)}
                              sx={{ 
                                transform: isExpanded ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s'
                              }}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </ListItemButton>
                      
                      {/* Expandable Student List */}
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List dense sx={{ pl: 4, pb: 1 }}>
                          {groupStudents.map((s: any) => (
                            <ListItemButton
                              key={s._id}
                              selected={isSelected(s._id, 'student')}
                              onClick={() => onSelect('student', s._id, `${s.firstName} ${s.lastName}`)}
                              sx={{
                                borderRadius: 1,
                                mb: 0.25,
                                py: 0.5,
                                '&.Mui-selected': {
                                  backgroundColor: 'primary.light',
                                  '&:hover': { backgroundColor: 'primary.light' },
                                },
                              }}
                            >
                              <Avatar sx={{ width: 24, height: 24, mr: 1.5, fontSize: '0.75rem' }}>
                                {(s.firstName?.[0] || '') + (s.lastName?.[0] || '')}
                              </Avatar>
                              <ListItemText
                                primary={`${s.firstName || ''} ${s.lastName || ''}`.trim()}
                                primaryTypographyProps={{ 
                                  variant: 'body2',
                                  fontWeight: isSelected(s._id, 'student') ? 600 : 400 
                                }}
                              />
                              <ArrowForwardIcon fontSize="small" sx={{ fontSize: 14, color: 'text.disabled', opacity: isSelected(s._id, 'student') ? 1 : 0 }} />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            )}
          </>
        )}
      </Box>

      <GroupEditDialog
        open={editingGroup !== null}
        onClose={() => setEditingGroup(null)}
        group={editingGroup}
        allStudents={students}
        onSaved={() => {
          setEditingGroup(null);
          loadData();
        }}
      />
    </Paper>
  );
};

export default RosterPanel;
